import {
  Dropdown,
  DropdownItem,
  DropdownList,
  Input,
  Paginate,
  Table,
  TableData,
  TableRow,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { FilterAltIcon } from 'cokitchen-icons';
import React, { useState, useEffect, useCallback } from 'react';
import './styles.scss';
import { useNavigate } from 'react-router-dom';
import BulkActions from './bulk-vendor-actions';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { debounce, formatCurrency } from 'utils/functions';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import usePricingContext from 'contexts/pricing.context';
import { CostSettingsType, MealPricingFormType, MealPricingType } from 'models/pricing.model';
import { DefaultCostSettings } from '../cost-settings';
import useRoleContext from 'contexts/role.context';
import { InventoryModule } from 'models/permission.model';
import { EDIT_PERMISSION_ACTIONS } from 'utils/authorization';

const MealsPricing = () => {
  const navigate = useNavigate();
  const { can } = useRoleContext();
  const { setStep, setMealPricingFormData, setSelectedMealPricing } = usePricingContext();
  const [selectedItems, setSelectedItems] = useState<MealPricingType[]>([]);
  const [mealsArray, setMealsArray] = useState<MealPricingType[]>([]);
  const [isFetchingMeals, setIsFetchingMeals] = useState(false);
  const [lastPage, setLastPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [costSettings, setCostSettings] = useState<CostSettingsType>(DefaultCostSettings);
  const [isFetchingCostSettings, setIsFetchingCostSettings] = useState(false);
  const canEditPricing = can(InventoryModule.MealAddOnPricing, EDIT_PERMISSION_ACTIONS);

  const hr_tax = parseFloat(costSettings.hr_tax);
  const vat = parseFloat(costSettings.vat) / 100;
  const labour_cost = parseFloat(costSettings.labour_cost);
  const gas_consumption = parseFloat(costSettings.gas_consumption);
  const min_selling_price_margin = parseFloat(costSettings.min_selling_price_margin);
  const projected_selling_price_margin = parseFloat(costSettings.projected_selling_price_margin);
  const TABLE_HEADERS = [
    'Meal',
    'Current selling price (N)',
    'Projected Selling Price',
    `Minimum Selling Price for Desired Margin (${min_selling_price_margin}%)`,
    'Total Cost excluding top line costs',
    `H&R Tax(${hr_tax}%)`,
    'Labour cost',
    'Gas Consumption',
    'Total Cost incl. top line costs',
    `VAT(${parseFloat(String(vat)) * 100}%)`,
    'Current Margin',
    'Earning',
    ''
  ];
  const isAllSelected = () => {
    return mealsArray.length > 0 && mealsArray.length === selectedItems.length;
  };
  const selectVendor = (id: string) => {
    const vendor = mealsArray.find((vendor) => vendor.ingredient.id === id);
    if (vendor) {
      if (selectedItems.includes(vendor)) {
        setSelectedItems(selectedItems.filter((vendor) => vendor.ingredient.id !== id));
      } else {
        setSelectedItems([...selectedItems, vendor]);
      }
    }
  };

  const [showBulkActions, setShowBulkActions] = useState<boolean>(true);

  const toggleAll = () => {
    if (!canEditPricing) return;
    if (isAllSelected()) {
      setSelectedItems([]);
    } else {
      setSelectedItems(mealsArray);
    }
  };
  const sortConvertionItem = (item: MealPricingType) => {
    const sortedArray: MealPricingFormType = {
      id: item?.id,
      ingredient: item?.ingredient,
      kitchen_price: '',
      old_kitchen_price: item?.kitchen_price,
      portion_type: item?.portion_type,
      no_of_portions: item?.no_of_portions
    };
    return sortedArray;
  };
  const handleEdit = (item?: MealPricingType) => {
    if (!canEditPricing) return;
    const items: MealPricingFormType[] = item
      ? [sortConvertionItem(item)]
      : selectedItems?.map((_) => sortConvertionItem(_));
    const itemsSelected = {
      ...items[0],
      itemIndex: 0
    };
    navigate(`${MAIN_STORE_ROUTE_KEYS.UPDATE_BASE_MEALS}`, {
      state: items
    });
    setMealPricingFormData(items);
    setSelectedMealPricing(itemsSelected);
    setStep(1);
  };

  useEffect(() => {
    if (selectedItems.length > 0) {
      setShowBulkActions(true);
    } else {
      setShowBulkActions(false);
    }
  }, [selectedItems]);

  const fetchMeals = async (
    searchString: string,
    categoryString: string,
    current_page?: number
  ) => {
    try {
      setIsFetchingMeals(true);
      const response = await apiService.get(`ingredient-mix/cokitchen/fetch-mixes`, {
        params: {
          ...(searchString ? { name: searchString } : {}),
          ...(categoryString ? { category: categoryString } : {}),
          page: current_page || '1'
        }
      });
      if (response) {
        setMealsArray(response?.data?.results || response?.data?.[0]?.mixes);
        setLastPage(Math.ceil((response?.data?.total || 1) / 10));
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching meals'}
          type='error'
        />
      );
    } finally {
      setIsFetchingMeals(false);
    }
  };

  const debounceFetchMeals = useCallback(debounce(fetchMeals, 1000), []);

  useEffect(() => {
    setIsFetchingMeals(true);
    clearVendorsList();
    debounceFetchMeals(search, category, currentPage);
    fetchCostSettings();
  }, [search, category, currentPage]);

  const fetchCostSettings = async () => {
    try {
      setIsFetchingCostSettings(true);
      const response = await apiService.get('pricing');
      if (response) {
        setCostSettings(response?.data);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching meals'}
          type='error'
        />
      );
    } finally {
      setIsFetchingCostSettings(false);
    }
  };

  const [categories, setCategories] = useState<any[]>([]);

  const fetchCategories = async (searchString: string) => {
    try {
      const response: any = await apiService.get(`ingredients/mix/categories`, {
        params: { ...(searchString ? { name: searchString } : {}), page: 1 }
      });
      const data = response?.data;
      if (data?.[0]) {
        setCategories([{ category: 'All' }, ...data]);
      }
    } catch (err: any) {
      console.log(err);
    }
  };

  const debouncefetchCategories = useCallback(debounce(fetchCategories, 500), []);

  useEffect(() => {
    debouncefetchCategories('');
  }, []);

  const clearVendorsList = () => {
    setMealsArray([]);
    setSelectedItems([]);
  };

  return (
    <div className='px-2 md:px-6 mt-2 mb-10 vendors'>
      <div className='flex justify-between items-center mb-8'>
        <div>
          <h4 className='text-[#2E3031] text-2xl font-semibold'>Pricing</h4>
          <p className='text-[#6F7376] text-sm'>Manage pricing of meals</p>
        </div>
      </div>
      <div className='flex justify-between items-center mb-8'>
        <div className='flex gap-3 items-center'>
          <Dropdown
            content={
              <div className='border border-[#E5E6E6] rounded-[5px] text-xs leading-[19px] h-[36px] flex px-2 justify-between items-center w-[114px]  cursor-pointer'>
                <span
                  className={`text-[#5E6164] text-sm capitalize whitespace-nowrap max-w-[calc(100%-25px)] overflow-hidden text-ellipsis`}>
                  {category || 'All'}
                </span>
                <FilterAltIcon className={`${category ? 'text-red-5' : 'text-[#5E6164]'}`} />
              </div>
            }>
            <DropdownList>
              {categories?.map((item: any, i) => (
                <DropdownItem
                  onClick={() => {
                    setCategory(item.category === 'All' ? '' : item.category);
                  }}
                  key={i}>
                  {item?.category}
                </DropdownItem>
              ))}
            </DropdownList>
          </Dropdown>
          <Input
            type='search'
            placeholder='Enter meal name'
            className='w-[300px] search-input'
            inputClass='h-[36px]'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Paginate
          pageCount={lastPage}
          pageRangeDisplayed={2}
          onPageChange={(e) => {
            clearVendorsList();
            setCurrentPage(e.selected + 1);
          }}
        />
      </div>
      <Table
        headers={TABLE_HEADERS}
        isAllSelected={isAllSelected()}
        onToggleAll={toggleAll}
        loading={isFetchingMeals || isFetchingCostSettings}
        canSelect={canEditPricing}
        pageCount={lastPage}>
        {mealsArray.map((item) => {
          const sellingPrice = parseFloat(item?.kitchen_price);
          const totalCostExcludingTopLineCost = parseFloat(
            item?.totalCostExcludingTopLineCost || '0'
          );
          const hrTaxCost = hr_tax * totalCostExcludingTopLineCost;
          const vatCost =
            vat * (hrTaxCost + gas_consumption + labour_cost + totalCostExcludingTopLineCost);
          const totalCostIncludingToplineCost =
            vatCost + hrTaxCost + gas_consumption + labour_cost + totalCostExcludingTopLineCost;
          const minimumSellingPrice = totalCostIncludingToplineCost * min_selling_price_margin;
          const projectedSellingPrice = minimumSellingPrice * projected_selling_price_margin;
          const totalCostIncludingVat = parseFloat(item?.totalCostIncludingVat || '0');
          const currentMargin =
            sellingPrice - (totalCostIncludingVat / totalCostIncludingVat) * 100;

          const earning = sellingPrice ? sellingPrice - totalCostIncludingVat : 0;
          return (
            <>
              <TableRow
                key={item.id}
                canSelect={canEditPricing}
                onToggle={() => {
                  if (!canEditPricing) return;
                  selectVendor(item.ingredient.id);
                }}
                className='text-gray-10'>
                <TableData className='text-gray-10'>{item?.ingredient.name}</TableData>
                <TableData>{formatCurrency(sellingPrice, { style: 'decimal' })}</TableData>
                <TableData>{formatCurrency(projectedSellingPrice, { style: 'decimal' })}</TableData>
                <TableData>{formatCurrency(minimumSellingPrice, { style: 'decimal' })}</TableData>
                <TableData>
                  {formatCurrency(totalCostExcludingTopLineCost, { style: 'decimal' })}
                </TableData>
                <TableData>{formatCurrency(hrTaxCost, { style: 'decimal' })}</TableData>
                <TableData>{formatCurrency(labour_cost, { style: 'decimal' })}</TableData>
                <TableData>{formatCurrency(gas_consumption, { style: 'decimal' })}</TableData>
                <TableData>
                  {formatCurrency(totalCostIncludingToplineCost, { style: 'decimal' })}
                </TableData>
                <TableData>{formatCurrency(vatCost, { style: 'decimal' })}</TableData>
                <TableData>{formatCurrency(currentMargin, { style: 'decimal' })}</TableData>
                <TableData>{formatCurrency(earning, { style: 'decimal' })}</TableData>
              </TableRow>
            </>
          );
        })}
      </Table>

      {canEditPricing && selectedItems.length !== 0 && (
        <BulkActions
          item={selectedItems}
          editFunc={handleEdit}
          showBulkActions={showBulkActions}
          setShowBulkActions={setShowBulkActions}
          setSelectedItems={setSelectedItems}
        />
      )}
    </div>
  );
};

export default MealsPricing;
